import {
  useAccessControl,
  useCurrentBusiness,
  useCurrentOutlet,
  useUserAuth,
} from '@billx/api';
import { Box, Divider, MenuItem, Typography } from '@mui/material';
import {
  LayoutListIcon,
  LogOutIcon,
  SendToBackIcon,
  UserRoundIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CustomMenu, UtilityIconButton } from '@/components/common';
import { getMenuLink } from '@/utilities';

export const ProfileSection = () => {
  const navigate = useNavigate();
  const { currentBusiness } = useCurrentBusiness();
  const { currentOutlet } = useCurrentOutlet();
  const { canAccessPage } = useAccessControl();
  const { logout } = useUserAuth();
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(
    null
  );

  if (!currentBusiness) {
    return null;
  }

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  const handleProfileClick = () => {
    navigate('/dashboard/profile');
    handleProfileMenuClose();
  };

  const handleStaffDashboardClick = () => {
    navigate('/staff');
    handleProfileMenuClose();
  };

  const handleMenuClick = () => {
    navigate(
      getMenuLink({
        businessId: currentBusiness?.id,
        outletId: currentOutlet?.id,
      })
    );
    handleProfileMenuClose();
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <UtilityIconButton
          onClick={handleProfileMenuOpen}
          tooltip={{ title: 'Profile' }}
        >
          <UserRoundIcon size={20} />
        </UtilityIconButton>
      </Box>

      {/* Profile Menu */}
      <CustomMenu
        anchorEl={profileAnchorEl}
        open={Boolean(profileAnchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.5,
            minWidth: 150,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {[
          {
            type: 'button',
            label: 'Profile',
            onClick: handleProfileClick,
            visible: true,
            icon: <UserRoundIcon size={18} />,
          },
          {
            type: 'button',
            label: 'Staff Dashboard',
            onClick: handleStaffDashboardClick,
            visible: canAccessPage('staff'),
            icon: <SendToBackIcon size={18} />,
          },
          {
            type: 'button',
            label: 'Menu',
            onClick: handleMenuClick,
            visible: true,
            icon: <LayoutListIcon size={18} />,
          },
          {
            type: 'divider',
            label: 'divider1',
          },
          {
            type: 'button',
            label: 'Sign Out',
            onClick: handleSignOut,
            color: 'error.main',
            visible: true,
            icon: <LogOutIcon size={16} />,
          },
        ].map((item) => {
          if (item.type === 'divider') {
            return <Divider key={item.label} />;
          }
          if (!item.visible) {
            return null;
          }
          return (
            <MenuItem
              key={item.label}
              onClick={item.onClick}
              sx={{
                py: { xs: 0.5, sm: 1.5 },
                color: item.color,
              }}
            >
              {item.icon}
              <Typography variant="body2" ml={1}>
                {item.label}
              </Typography>
            </MenuItem>
          );
        })}
      </CustomMenu>
    </>
  );
};
